import { Box, Skeleton, Typography } from '@mui/material'
import moment from 'moment'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import GraphCard from 'packages/ui/Graph/Card'
import Legend from 'packages/ui/Graph/Legend'
import TooltipList from 'packages/ui/Graph/TooltipList'

import { type DailyEmailCount } from '../types'
import { formatNumber } from '../utils'

type DailyEmailsChartProps = {
  title: string
  data: DailyEmailCount[]
  color: string
  loading: boolean
}

type ChartTooltipProps = {
  active?: boolean
  payload?: Array<{
    value?: number
    payload?: DailyEmailCount
  }>
  color: string
}

const ChartTooltip = ({ active, payload = [], color }: ChartTooltipProps) => {
  const dailyEmailCount = payload[0]

  if (!active || !dailyEmailCount?.payload) {
    return null
  }

  return (
    <TooltipList
      title={moment(dailyEmailCount.payload.date).format('DD/MM/YYYY')}
      list={[
        {
          color,
          label: 'Mails envoyés',
          value: formatNumber(Number(dailyEmailCount.value ?? 0))
        }
      ]}
    />
  )
}

const DailyEmailsChart = ({ title, data, color, loading }: DailyEmailsChartProps) => {
  const hasData = data.some(({ email_count: emailCount }) => emailCount > 0)

  return (
    <GraphCard
      title={title}
      sx={{ borderRadius: '24px', minWidth: 0 }}
      legend={<Legend color={color} label="Mails envoyés" />}
    >
      {loading && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={260}
          sx={{ borderRadius: '16px', mt: 2 }}
        />
      )}

      {!loading && !hasData && (
        <Box
          sx={{
            minHeight: 260,
            mt: 2,
            border: '1px dashed #D7DAEA',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ color: '#989898', fontSize: 12, fontWeight: 500 }}>
            Aucun mail envoyé sur cette période
          </Typography>
        </Box>
      )}

      {!loading && hasData && (
        <Box sx={{ width: '100%', height: 260, mt: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              barCategoryGap="20%"
            >
              <XAxis
                dataKey="date"
                tickFormatter={(date) => moment(date).format('DD/MM')}
                minTickGap={24}
                tick={{ fill: '#989898', fontSize: 10 }}
                axisLine={{ stroke: '#EFEFEF' }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fill: '#989898', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: '#F7F7F7', radius: 6 }}
                wrapperStyle={{ outline: 'none', zIndex: 9999 }}
                content={<ChartTooltip color={color} />}
              />
              <Bar
                dataKey="email_count"
                fill={color}
                radius={[4, 4, 4, 4]}
                maxBarSize={16}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </GraphCard>
  )
}

export default DailyEmailsChart
